import { useState, useMemo, useEffect, useCallback } from 'react';
import { passwordValidator } from '../components/auth-validators';
import { useFormValidation } from './useFormValidation';

export interface PasswordRequirement {
  label: string;
  isValid: boolean;
}

const PASSWORD_REQUIREMENTS: Array<{ label: string; test: (password: string) => boolean }> = [
  {
    label: 'Minimum 8 caractères',
    test: (password) => password.length >= 8,
  },
  {
    label: 'Une majuscule',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: 'Un chiffre',
    test: (password) => /[0-9]/.test(password),
  },
  {
    label: 'Un caractère spécial',
    test: (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  },
];

export interface UsePasswordValidationReturn {
  password: string;
  confirmPassword: string;
  passwordError: string;
  confirmPasswordError: string;
  passwordRequirements: PasswordRequirement[];
  allRequirementsMet: boolean;
  passwordsMatch: boolean;
  isButtonEnabled: boolean;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  handlePasswordChange: (value: string) => void;
  handleConfirmPasswordChange: (value: string) => void;
  handlePasswordBlur: () => void;
  handleConfirmPasswordBlur: () => void;
  validate: () => boolean;
}

/**
 * Hook spécialisé pour la validation de mot de passe avec confirmation
 */
export function usePasswordValidation(): UsePasswordValidationReturn {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [hasConfirmBlurred, setHasConfirmBlurred] = useState(false);
  const [hasConfirmAttemptedSubmit, setHasConfirmAttemptedSubmit] = useState(false);

  const passwordValidation = useFormValidation({
    initialValue: '',
    rules: [passwordValidator],
  });

  const validateConfirmPassword = useCallback((value: string): string | null => {
    if (value && password && value !== password) {
      return 'Les mots de passe ne correspondent pas';
    }
    return null;
  }, [password]);

  useEffect(() => {
    if (confirmPassword && (hasConfirmBlurred || hasConfirmAttemptedSubmit)) {
      const error = validateConfirmPassword(confirmPassword);
      setConfirmPasswordError(error || '');
    }
  }, [password, confirmPassword, hasConfirmBlurred, hasConfirmAttemptedSubmit, validateConfirmPassword]);

  const passwordRequirements = useMemo<PasswordRequirement[]>(() => {
    return PASSWORD_REQUIREMENTS.map((req) => ({
      label: req.label,
      isValid: req.test(password),
    }));
  }, [password]);

  const allRequirementsMet = useMemo(() => {
    return passwordRequirements.every((req) => req.isValid);
  }, [passwordRequirements]);

  const passwordsMatch = useMemo(() => {
    return password === confirmPassword && confirmPassword.length > 0;
  }, [password, confirmPassword]);

  const isButtonEnabled = allRequirementsMet && passwordsMatch;

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    passwordValidation.handleChange(value);
    if (confirmPassword && (hasConfirmBlurred || hasConfirmAttemptedSubmit)) {
      const error = validateConfirmPassword(confirmPassword);
      setConfirmPasswordError(error || '');
    }
  }, [confirmPassword, hasConfirmBlurred, hasConfirmAttemptedSubmit, validateConfirmPassword, passwordValidation]);

  const handleConfirmPasswordChange = useCallback((value: string) => {
    setConfirmPassword(value);
    
    if (hasConfirmAttemptedSubmit || hasConfirmBlurred) {
      const error = validateConfirmPassword(value);
      setConfirmPasswordError(error || '');
    } else {
      setConfirmPasswordError('');
    }
  }, [hasConfirmAttemptedSubmit, hasConfirmBlurred, validateConfirmPassword]);

  const handleConfirmPasswordBlur = useCallback(() => {
    setHasConfirmBlurred(true);
    const error = validateConfirmPassword(confirmPassword);
    setConfirmPasswordError(error || '');
  }, [confirmPassword, validateConfirmPassword]);

  const validate = useCallback((): boolean => {
    setHasConfirmAttemptedSubmit(true);
    passwordValidation.setHasAttemptedSubmit(true);
    
    const passwordError = passwordValidation.validate();
    const confirmError = validateConfirmPassword(confirmPassword);
    
    if (passwordError) {
      passwordValidation.setError(passwordError);
    }
    if (confirmError) {
      setConfirmPasswordError(confirmError);
    }
    
    return !passwordError && !confirmError && isButtonEnabled;
  }, [passwordValidation, validateConfirmPassword, confirmPassword, isButtonEnabled]);

  return {
    password,
    confirmPassword,
    passwordError: passwordValidation.error,
    confirmPasswordError,
    passwordRequirements,
    allRequirementsMet,
    passwordsMatch,
    isButtonEnabled,
    setPassword,
    setConfirmPassword,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handlePasswordBlur: passwordValidation.handleBlur,
    handleConfirmPasswordBlur,
    validate,
  };
}

